import React, {useMemo, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import {DndContext, DragEndEvent, closestCenter} from "@dnd-kit/core";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {SortableContext, arrayMove, verticalListSortingStrategy} from "@dnd-kit/sortable";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import {GridColDef} from "@mui/x-data-grid";
import {toast} from "sonner";
import {Box, Chip, Colors, Text} from "@bounce/shared/ui";
import AreYouSureDialog from "@bounce/shared/consumer/bounce-ui/components/Dialogs/AreYouSureDialog";
import {Button, ListDataGrid, ListPageHeader, RowActions, SearchInput, Tooltip} from "../../ui/components";
import {useArchivePromo} from "../../apis/requests/promos/archivePromo";
import {useGetPromos} from "../../apis/requests/promos/getPromos";
import {useOrderPromos} from "../../apis/requests/promos/orderPromos";
import {ReachPromo} from "../../apis/requests/promos/types";
import {useUpdatePromo} from "../../apis/requests/promos/updatePromo";
import {
  DndEnabledContext,
  DragHandleCell,
  ReorderChangesBar,
  SortableGridRow,
  useRowOrder,
  useSortableSensors,
} from "../../components/sortable";
import PromoDialog from "./PromoDialog";
import SegmentedToggle from "./promo-form/SegmentedToggle";
import {BOARD_TABS, BoardTab, PROMO_STATUSES, STATUS_DISPLAY, SettablePromoStatus} from "./promo-form/constants";
import {formValuesToPayload, promoIssues, promoToFormValues, todayDateInput} from "./promo-form/helpers";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 100;
const RANK_BADGE_SIZE = 28;
const PAGE_TITLE = "Promo Board";
const NEEDS_FIXING_LABEL = "Needs fixing";
const ARCHIVE_LABEL = "Archive";
const STATUS_ACTIONS: Record<SettablePromoStatus, {icon: React.ReactNode; description: string}> = {
  draft: {
    icon: <EditNoteOutlinedIcon fontSize="small" />,
    description:
      "Changing the status changes the promo's account assignments: all accounts in the promo are removed from it and their offers go back to normal.",
  },
  active: {
    icon: <PlayArrowOutlinedIcon fontSize="small" />,
    description:
      "Changing the status changes the promo's account assignments: matching accounts start entering the promo, get its offers, and receive its day-plan messages.",
  },
  ended: {
    icon: <StopCircleOutlinedIcon fontSize="small" />,
    description:
      "Changing the status changes the promo's account assignments: all accounts in the promo are removed from it and their offers go back to normal.",
  },
};
const DRAG_ACTIVATION_DISTANCE = 8;

const tabLabel = (tab: BoardTab, promos: ReachPromo[]) => {
  const count = tab === "all" ? promos.length : promos.filter((promo) => promo.status === tab).length;
  const label = tab === "all" ? "All" : STATUS_DISPLAY[tab].label;
  return `${label} ${count}`;
};

const PromosPage = () => {
  const {data, isLoading, error} = useGetPromos({count_accounts: true});
  const {mutate: orderPromos, isPending: isReordering} = useOrderPromos();
  const {mutateAsync: archiveAsync} = useArchivePromo();
  const {mutateAsync: updateAsync} = useUpdatePromo();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<BoardTab>("all");
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: DEFAULT_PAGE_SIZE});

  const sensors = useSortableSensors(DRAG_ACTIVATION_DISTANCE);

  const promos = useMemo(() => data ?? [], [data]);
  const promoById = useMemo(() => new Map(promos.map((promo) => [promo.id, promo])), [promos]);
  const baselineIds = useMemo(() => promos.map((promo) => promo.id), [promos]);

  const {orderedIds, hasChanges: hasReorderChanges, reorder, discard} = useRowOrder(baselineIds);

  const orderedPromos = useMemo(
    () => orderedIds.map((id) => promoById.get(id)).filter((promo): promo is ReachPromo => Boolean(promo)),
    [orderedIds, promoById]
  );
  const priorityById = useMemo(
    () => new Map(orderedPromos.map((promo, index) => [promo.id, index + 1])),
    [orderedPromos]
  );

  const visiblePromos = useMemo(() => {
    const query = search.trim().toLowerCase();
    return orderedPromos.filter(
      (promo) => (tab === "all" || promo.status === tab) && (!query || promo.name.toLowerCase().includes(query))
    );
  }, [orderedPromos, search, tab]);

  const dndEnabled = tab === "all" && !search.trim();

  const openPromo = (promo?: ReachPromo) => NiceModal.show(PromoDialog, {promo});

  const pageRowIds = useMemo(() => {
    const {page, pageSize} = paginationModel;
    return visiblePromos.slice(page * pageSize, (page + 1) * pageSize).map((promo) => promo.id);
  }, [visiblePromos, paginationModel]);

  const handleDragEnd = ({active, over}: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = orderedIds.indexOf(Number(active.id));
    const newIndex = orderedIds.indexOf(Number(over.id));
    reorder(arrayMove(orderedIds, oldIndex, newIndex));
  };

  const handleSaveOrder = () =>
    orderPromos(
      {promo_ids: orderedIds},
      {
        onSuccess: () => {
          toast.success("Promo priorities updated");
          discard();
        },
      }
    );

  const setStatus = (promo: ReachPromo, status: SettablePromoStatus) =>
    updateAsync({
      id: promo.id,
      payload: formValuesToPayload({...promoToFormValues(promo), status}, todayDateInput()),
    });

  const onChangeStatus = (promo: ReachPromo, status: SettablePromoStatus) =>
    NiceModal.show(AreYouSureDialog, {
      headerTextId: `Move "${promo.name}" to ${STATUS_DISPLAY[status].label}?`,
      contentTextId: STATUS_ACTIONS[status].description,
      confirmTextId: STATUS_DISPLAY[status].label,
      onConfirm: async () => {
        await setStatus(promo, status);
        toast.success(`Promo "${promo.name}" moved to ${STATUS_DISPLAY[status].label}`);
      },
    });

  const onArchive = (promo: ReachPromo) =>
    NiceModal.show(AreYouSureDialog, {
      headerTextId: `Archive "${promo.name}"?`,
      contentTextId: "The promo leaves the board and stops running. Its key can be reused by a new promo.",
      confirmTextId: ARCHIVE_LABEL,
      onConfirm: async () => {
        await archiveAsync({id: promo.id});
        toast.success(`Promo "${promo.name}" archived`);
      },
    });

  const columns = useMemo<GridColDef<ReachPromo>[]>(
    () => [
      {
        field: "__drag__",
        headerName: "",
        width: 48,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: () => <DragHandleCell />,
      },
      {
        field: "priority",
        headerName: "#",
        width: 70,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({row}) => (
          <Box
            sx={{
              width: RANK_BADGE_SIZE,
              height: RANK_BADGE_SIZE,
              borderRadius: "8px",
              backgroundColor: Colors.tertiary._100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text size={12} variant="inter600" sx={{color: Colors.grey._700}}>
              {priorityById.get(row.id)}
            </Text>
          </Box>
        ),
      },
      {
        field: "name",
        headerName: "Promo",
        flex: 2,
        renderCell: ({row}) => (
          <Text size={14} variant="inter600" sx={{color: Colors.grey._900}}>
            {row.name}
          </Text>
        ),
      },
      {
        field: "segments",
        headerName: "Segment",
        flex: 2,
        sortable: false,
        renderCell: ({row}) => (
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, py: 1}}>
            {row.segments.map((segment) => (
              <Chip key={segment.id} size_="small" variant_="blue" label={segment.name} />
            ))}
          </Box>
        ),
      },
      {
        field: "matching_accounts_count",
        headerName: "Accounts",
        width: 110,
        sortable: false,
        renderCell: ({row}) => (
          <Text size={14} sx={{color: Colors.grey._700}}>
            {row.matching_accounts_count?.toLocaleString() ?? "—"}
          </Text>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        sortable: false,
        renderCell: ({row}) => {
          const issues = promoIssues(row);
          return (
            <Box sx={{display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5, py: 1}}>
              <Chip
                size_="small"
                variant_={STATUS_DISPLAY[row.status].variant}
                label={STATUS_DISPLAY[row.status].label}
              />
              {issues.length > 0 && (
                <Tooltip title={issues.join(" ")}>
                  <span>
                    <Chip size_="small" variant_="error" label={NEEDS_FIXING_LABEL} />
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 72,
        sortable: false,
        renderCell: ({row}) => (
          <RowActions
            onEdit={() => openPromo(row)}
            extraActions={[
              ...PROMO_STATUSES.map((status) => ({
                label: STATUS_DISPLAY[status].label,
                icon: STATUS_ACTIONS[status].icon,
                disabled: row.status === status,
                onClick: () => onChangeStatus(row, status),
              })),
              {
                label: ARCHIVE_LABEL,
                icon: <ArchiveOutlinedIcon fontSize="small" />,
                onClick: () => onArchive(row),
              },
            ]}
          />
        ),
      },
    ],
    [priorityById]
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title={PAGE_TITLE}>
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPaginationModel((model) => ({...model, page: 0}));
          }}
          placeholder="Search promos..."
        />
        <Button buttonType="contained-primary" size="small" onClick={() => openPromo()}>
          + Add promo
        </Button>
      </ListPageHeader>
      <Box sx={{mb: 2}}>
        <SegmentedToggle
          value={tab}
          options={BOARD_TABS.map((value) => ({value, label: tabLabel(value, promos)}))}
          onChange={(next) => {
            setTab(next);
            setPaginationModel((model) => ({...model, page: 0}));
          }}
        />
      </Box>
      {hasReorderChanges && <ReorderChangesBar onSave={handleSaveOrder} onDiscard={discard} disabled={isReordering} />}
      {error ? (
        <Text size={14}>Failed to load promos.</Text>
      ) : (
        <DndEnabledContext.Provider value={dndEnabled}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={pageRowIds} strategy={verticalListSortingStrategy}>
              <ListDataGrid
                rows={visiblePromos}
                columns={columns}
                loading={isLoading}
                getRowId={(row) => row.id}
                onRowClick={(params) => openPromo(promoById.get(Number(params.id)))}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                slots={{row: SortableGridRow}}
                noRowsText={search.trim() || tab !== "all" ? "No promos match this view" : "No promos yet"}
              />
            </SortableContext>
          </DndContext>
        </DndEnabledContext.Provider>
      )}
    </Box>
  );
};

export default PromosPage;
